// src/contexts/AblyMinimal.js - Versión mejorada

import { Realtime } from 'ably';

// Variable global para mantener una única instancia de Ably
let ablyInstance = null;

// Para prevenir duplicación de mensajes
let processedMessageIds = new Set();
// Para llevar un registro de contactos ya suscritos
let subscribedContacts = new Set();
// Para rastrear mensajes que ya incrementaron el contador
let countedMessages = new Set();
// Cache global para media ya cargado (imágenes, videos, audio)
let processedMediaCache = new Map();

// Función para iniciar una conexión simple a Ably
export function iniciarConexionAbly(contactId, setMessagesUser = null, setContacts = null) {
    try {
        // Si ya existe una instancia, devolverla en lugar de crear una nueva
        if (ablyInstance) {
            // Si se proporciona un contactId, actualizar el contacto activo
            if (contactId) {
                window.activeContactId = contactId.toString();
            }

            return ablyInstance;
        }

        // Limpiar sets al iniciar una nueva instancia
        processedMessageIds.clear();
        subscribedContacts.clear();
        countedMessages.clear();
        // No limpiamos processedMediaCache para mantener la caché de medios entre conexiones

        // Crear instancia de Ably con configuración optimizada
        const ably = new Realtime({
            key: "T7gK-A.K5eh1A:ewzlrOYsHVGVpNhutpb-hi2gyQTY9PhejB2Eas5Z8xE",
            closeOnUnload: true,
            logLevel: 1, // Solo errores
            clientId: `cliente-${Date.now()}`,

            // Configuración para reducir reconexiones
            idleTimeout: 180000, // 3 minutos
            disconnectedRetryTimeout: 15000,
            suspendedRetryTimeout: 30000,
            recover: null
        });

        // Eventos de conexión - minimizando logs
        ably.connection.once('connected', () => {
            // Conexión establecida
        });

        // Función para procesar mensajes evitando duplicación
        const processMessage = (contactId, messageData) => {
            try {
                if (!contactId || !messageData) return false;

                const contactIdStr = contactId.toString();
                
                // Si es un nuevo mensaje
                if (messageData.typeMessage === 'new') {
                    // Verificar duplicación usando ID del mensaje
                    const messageKey = `${messageData.id}`;
                    if (processedMessageIds.has(messageKey)) {
                        return false; // Evitar procesar el mismo mensaje dos veces
                    }

                    // Registrar que este mensaje ya fue procesado
                    processedMessageIds.add(messageKey);

                    // Limpiar mensajes antiguos del Set (más de 5 minutos)
                    setTimeout(() => {
                        processedMessageIds.delete(messageKey);
                    }, 300000); // 5 minutos

                    // Pre-cachear multimedia si existe
                    if (messageData.type === 'image' || messageData.type === 'video' || 
                        messageData.type === 'audio' || messageData.type === 'document') {
                        if (messageData.media_url && !processedMediaCache.has(messageData.media_url)) {
                            // Marcar como procesado inmediatamente para evitar recargas
                            processedMediaCache.set(messageData.media_url, true);
                            
                            // Si es imagen, precargar
                            if (messageData.type === 'image') {
                                const img = new Image();
                                img.src = messageData.media_url;
                            }
                            // Para otros tipos podríamos implementar precarga específica
                        }
                    }

                    // Solo actualizar UI si es el contacto activo
                    if (setMessagesUser && window.activeContactId === contactIdStr) {
                        const newMsg = {
                            id: messageData.id,
                            text: messageData.text || messageData.content || '',
                            sender: messageData.sender || 'contact',
                            timestamp: new Date(messageData.timestamp || Date.now()),
                            status: messageData.status || 'received',
                            type: messageData.type || 'text',
                            media_url: messageData.media_url || '',
                            caption: messageData.caption || ''
                        };

                        setMessagesUser(prevMessages => {
                            // Verificar duplicados en el estado actual
                            if (prevMessages.some(m => m.id === newMsg.id)) {
                                return prevMessages;
                            }

                            // Disparar evento solo si realmente es un mensaje nuevo
                            try {
                                const newMessageEvent = new CustomEvent('new-message-received', {
                                    detail: { contactId: contactIdStr, messageId: messageData.id }
                                });
                                window.dispatchEvent(newMessageEvent);
                            } catch (eventError) {
                                // Error al disparar evento
                            }

                            return [...prevMessages, newMsg];
                        });

                        // Actualizar el lastMessageTime cuando el contacto está activo
                        updateContactTimestamp(contactIdStr, messageData.timestamp || Date.now());
                    } else {
                        // Si no es el contacto activo, incrementar contador (UNA SOLA VEZ)
                        if (window.activeContactId !== contactIdStr) {
                            // Verificar si ya incrementamos el contador para este mensaje
                            const countKey = `count-${messageData.id}`;
                            if (!countedMessages.has(countKey)) {
                                // Marcar como contado
                                countedMessages.add(countKey);

                                // Programar limpieza
                                setTimeout(() => {
                                    countedMessages.delete(countKey);
                                }, 300000); // 5 minutos

                                // Actualizar contador y el timestamp
                                updateContactUnreadCount(contactIdStr, messageData.timestamp || Date.now());
                            }
                        }
                    }
                }
                // Si es actualización de estado
                else if (messageData.typeMessage === 'status-update' && messageData.messages) {
                    // Solo actualizar UI si es el contacto activo
                    if (setMessagesUser && window.activeContactId === contactIdStr) {
                        setMessagesUser(prevMessages => {
                            // Importante: Usar clonación inmutable para evitar problemas de renderizado
                            const updatedMessages = [...prevMessages];
                            let changed = false;
                            
                            for (let i = 0; i < updatedMessages.length; i++) {
                                const msg = updatedMessages[i];
                                const updateInfo = messageData.messages.find(m => m.id === msg.id);
                                
                                if (updateInfo && msg.status !== updateInfo.status) {
                                    // Crear una copia nueva del mensaje para forzar re-renderizado
                                    // pero MANTENER las referencias de media_url y otros datos
                                    updatedMessages[i] = { 
                                        ...msg, 
                                        status: updateInfo.status,
                                        // Importante: añadir un campo que indique que es una actualización
                                        _statusUpdated: Date.now()
                                    };
                                    changed = true;
                                }
                            }
                            
                            // Solo devolver un nuevo array si realmente hubo cambios
                            return changed ? updatedMessages : prevMessages;
                        });
                    }
                }

                return true;
            } catch (error) {
                return false;
            }
        };

        // Función para actualizar contador, timestamp y ordenar contactos
        const updateContactUnreadCount = (contactId, timestamp) => {
            if (!setContacts) return;

            setContacts(prevContacts => {
                if (!prevContacts || !Array.isArray(prevContacts)) return prevContacts;

                // Primero actualizamos el contacto específico
                const updatedContacts = prevContacts.map(contact => {
                    if (contact && contact.id && contact.id.toString() === contactId.toString()) {
                        // Incrementar contador y actualizar timestamp
                        return {
                            ...contact,
                            received_messages_count: (contact.received_messages_count || 0) + 1,
                            lastMessageTime: timestamp, // Actualizar con el timestamp del mensaje
                        };
                    }
                    return contact;
                });

                // Ordenar por lastMessageTime descendente (más recientes primero)
                return sortContactsByLastMessage(updatedContacts);
            });
        };

        // Función para actualizar solo el timestamp sin incrementar contador
        const updateContactTimestamp = (contactId, timestamp) => {
            if (!setContacts) return;

            setContacts(prevContacts => {
                if (!prevContacts || !Array.isArray(prevContacts)) return prevContacts;

                // Primero actualizamos el contacto específico
                const updatedContacts = prevContacts.map(contact => {
                    if (contact && contact.id && contact.id.toString() === contactId.toString()) {
                        // Solo actualizar timestamp sin modificar contador
                        return {
                            ...contact,
                            lastMessageTime: timestamp, // Actualizar con el timestamp del mensaje
                        };
                    }
                    return contact;
                });

                // Ordenar por lastMessageTime descendente (más recientes primero)
                return sortContactsByLastMessage(updatedContacts);
            });
        };

        // Función auxiliar para ordenar contactos por lastMessageTime
        const sortContactsByLastMessage = (contacts) => {
            return [...contacts].sort((a, b) => {
                // Si alguno no tiene lastMessageTime, ponerlo al final
                if (!a.lastMessageTime) return 1;
                if (!b.lastMessageTime) return -1;

                // Convertir a timestamps para comparación
                const timeA = typeof a.lastMessageTime === 'string'
                    ? new Date(a.lastMessageTime).getTime()
                    : a.lastMessageTime;

                const timeB = typeof b.lastMessageTime === 'string'
                    ? new Date(b.lastMessageTime).getTime()
                    : b.lastMessageTime;

                // Ordenar de más reciente a más antiguo
                return timeB - timeA;
            });
        };
        // Función global para acceder al contacto activo
        window.setActiveContactId = (id) => {
            window.activeContactId = id ? id.toString() : null;
        };

        // Establecer contacto activo inicial si se proporciona
        if (contactId) {
            window.activeContactId = contactId.toString();
        }

        // Almacenar los canales activos
        const activeChannels = {};

        // Suscribirse a canales individuales para cada contacto
        const subscribeToContactChannel = (contactId) => {
            try {
                const contactIdStr = contactId.toString();

                // PRIMERA VERIFICACIÓN: Usar un ID único para cada suscripción
                const subscriptionKey = `sub-${contactIdStr}`;

                // Si ya estamos suscritos a este contacto, retornar canal existente
                if (subscribedContacts.has(subscriptionKey)) {
                    return activeChannels[contactIdStr] || null;
                }

                // REGISTRAR ANTES de hacer cualquier operación
                subscribedContacts.add(subscriptionKey);

                // Si el canal ya existe, reutilizarlo (no debería ocurrir)
                if (activeChannels[contactIdStr]) {
                    return activeChannels[contactIdStr];
                }

                // Crear nuevo canal
                const channelName = `messages-channel-${contactIdStr}`;
                const channel = ably.channels.get(channelName);
                activeChannels[contactIdStr] = channel;

                // Suscribirse a nuevos mensajes - UNA SOLA VEZ
                const newMessageHandler = (message) => {
                    if (message.data) {
                        const messageData = {
                            typeMessage: 'new',
                            ...message.data
                        };
                        processMessage(contactIdStr, messageData);
                    }
                };

                // Suscribirse a actualizaciones de estado - UNA SOLA VEZ
                const statusUpdateHandler = (message) => {
                    if (message.data && message.data.messages) {
                        const messageData = {
                            typeMessage: 'status-update',
                            messages: message.data.messages
                        };
                        processMessage(contactIdStr, messageData);
                    }
                };

                // Registrar manejadores usando once() para evitar duplicación
                channel.subscribe('new-message', newMessageHandler);
                channel.subscribe('status-update', statusUpdateHandler);

                return channel;
            } catch (err) {
                return null;
            }
        };

        // Función para desuscribirse de un contacto
        const unsubscribeFromContact = (contactId) => {
            try {
                const contactIdStr = contactId.toString();
                const subscriptionKey = `sub-${contactIdStr}`;

                // Eliminar de la lista de suscritos
                subscribedContacts.delete(subscriptionKey);

                if (activeChannels[contactIdStr]) {
                    const channel = activeChannels[contactIdStr];
                    channel.unsubscribe();
                    ably.channels.release(`messages-channel-${contactIdStr}`);
                    delete activeChannels[contactIdStr];
                    return true;
                }

                return false;
            } catch (err) {
                return false;
            }
        };

        // Crear objeto para controlar la conexión
        const conexion = {
            ably,
            activeChannels,
            subscribeToContact: subscribeToContactChannel,
            unsubscribeFromContact,
            setActiveContact: window.setActiveContactId,
            // Añadir método para verificar si un media ya está en caché
            isMediaCached: (url) => processedMediaCache.has(url),
            // Añadir método para añadir manualmente una URL al caché
            addToMediaCache: (url) => {
                if (url) processedMediaCache.set(url, true);
            },
            cerrar: () => {
                // Desuscribirse de todos los canales
                Object.keys(activeChannels).forEach(contactId => {
                    try {
                        unsubscribeFromContact(contactId);
                    } catch (err) {
                        // Error al desuscribirse
                    }
                });

                // Limpiar listas
                processedMessageIds.clear();
                subscribedContacts.clear();
                countedMessages.clear();
                // NO limpiamos processedMediaCache para mantener la caché de medios

                // Cerrar conexión
                try {
                    ably.close();
                } catch (err) {
                    // Error al cerrar
                }

                // Limpiar instancia global
                ablyInstance = null;
            }
        };

        // Guardar en variable global
        ablyInstance = conexion;

        return conexion;
    } catch (error) {
        ablyInstance = null;
        return null;
    }
}

// Añadir método global para facilitar el acceso al cache
export function isMediaCached(url) {
    return processedMediaCache.has(url);
}

// Añadir método global para agregar al cache manualmente
export function addToMediaCache(url) {
    if (url) {
        processedMediaCache.set(url, true);
        return true;
    }
    return false;
}